import React, { useState, useEffect, useCallback } from 'react';
import { getClientes, deleteCliente } from '../../../api/clientes';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, IconButton, Dialog, DialogTitle, DialogContent, TextField, CircularProgress, TablePagination, Box } from '@mui/material';
import { Edit, Delete, Visibility, Add, Update, Inventory2 } from '@mui/icons-material';
import CreateCliente from './_Create';
import EditCliente from './_Edit';
import ViewCliente from './_ViewById';
import AssignProductos from './_AssignProductos';
import { ConfirmDelete } from '../../../components/ConfirmDelete';
import { getTipoIdentificaciones } from '../../../api/tipoIdentificacion';
import { useAuth } from '../../../context/auth';

const Clientes = () => {
  const [clientes, setClientes] = useState([]);
  const [openCreate, setOpenCreate] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [openView, setOpenView] = useState(false);
  const [openProductos, setOpenProductos] = useState(false);
  const [selectedCliente, setSelectedCliente] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const { permisos } = useAuth();
  const { Prm_Config_Cltes_Crear, Prm_Config_Cltes_Modificar, Prm_Config_Cltes_Eliminar } = permisos;

  const [openConfirmDelete, setOpenConfirmDelete] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [lstTipoIdentificacion, setLstTipoIdentificacion] = useState([]);
  const handleOpenDeleteConfirm = useCallback((id: number) => {
    setDeleteId(id);
    setOpenConfirmDelete(true);
  }, []);

  const handleCloseDeleteConfirm = useCallback(() => {
    setOpenConfirmDelete(false);
    setDeleteId(null);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (deleteId !== null) {
      await deleteCliente(deleteId);
      fetchClientes();
      handleCloseDeleteConfirm();
    }
  }, [deleteId, handleCloseDeleteConfirm]);

  useEffect(() => {
    fetchClientes();
    fetchTipoIdentificaciones();
  }, []);

  const fetchClientes = useCallback(async () => {
    setLoading(true);
    const response = await getClientes();
    if (response.isOk) {
      setClientes(response.data);
    }
    setLoading(false);
  }, []);

  const fetchTipoIdentificaciones = useCallback(async () => {
    setLoading(true);
    const response = await getTipoIdentificaciones();
    if (response.isOk) {
      setLstTipoIdentificacion(response.data);
    }
    setLoading(false);
  }, []);

  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  }, []);

  const handleChangePage = useCallback((event: unknown, newPage: number) => {
    setPage(newPage);
  }, []);

  const handleChangeRowsPerPage = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  }, []);

  const filteredClientes = clientes.filter((cliente: any) => cliente.Cl_Nombre.toLowerCase().includes(searchTerm.toLowerCase()) || cliente.Cl_Documento.toLowerCase().includes(searchTerm.toLowerCase()) || cliente.Cl_Correo.toLowerCase().includes(searchTerm.toLowerCase()));

  return (
    <div style={{ marginTop: '5px' }}>
      {loading ? (
        <Box display='flex' justifyContent='center' alignItems='center' height='100%'>
          <CircularProgress />
        </Box>
      ) : (
        <div style={{ marginTop: '5px' }}>
          <Box display='flex' justifyContent='space-between' alignItems='center' mb={2}>
            <TextField label='Buscar' value={searchTerm} onChange={handleSearchChange} fullWidth size='small' />
            {Prm_Config_Cltes_Crear === 1 && (
              <IconButton onClick={() => setOpenCreate(true)} title='Nuevo' className='tool-btn'>
                <Add color='success' />
              </IconButton>
            )}
            <IconButton onClick={fetchClientes} title='Actualizar' className='tool-btn'>
              <Update />
            </IconButton>
          </Box>
          <TableContainer component={Paper}>
            <Table size='small'>
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Nombre</TableCell>
                  <TableCell>Documento</TableCell>
                  <TableCell>Teléfono</TableCell>
                  <TableCell>Correo</TableCell>
                  <TableCell>Dirección</TableCell>
                  <TableCell>Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredClientes.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((cliente: any) => (
                  <TableRow key={cliente.Cl_Id}>
                    <TableCell>{cliente.Cl_Id}</TableCell>
                    <TableCell>{cliente.Cl_Nombre}</TableCell>
                    <TableCell>{cliente.Cl_Documento}</TableCell>
                    <TableCell>{cliente.Cl_Telefono}</TableCell>
                    <TableCell>{cliente.Cl_Correo}</TableCell>
                    <TableCell>{cliente.Cl_Direccion}</TableCell>
                    <TableCell>
                      <IconButton
                        onClick={() => {
                          setSelectedCliente(cliente);
                          setOpenView(true);
                        }}>
                        <Visibility color='info' />
                      </IconButton>
                      {Prm_Config_Cltes_Modificar === 1 && (
                        <IconButton
                          onClick={() => {
                            setSelectedCliente(cliente);
                            setOpenEdit(true);
                          }}>
                          <Edit color='primary' />
                        </IconButton>
                      )}
                      {Prm_Config_Cltes_Eliminar === 1 && (
                        <IconButton onClick={() => handleOpenDeleteConfirm(cliente.Cl_Id)} title='Eliminar'>
                          <Delete color='error' />
                        </IconButton>
                      )}
                      {Prm_Config_Cltes_Modificar === 1 && (
                        <IconButton
                          onClick={() => {
                            setSelectedCliente(cliente);
                            setOpenProductos(true);
                          }}
                          title='Productos asignados'>
                          <Inventory2 color='action' />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination component='div' count={filteredClientes.length} page={page} onPageChange={handleChangePage} rowsPerPage={rowsPerPage} onRowsPerPageChange={handleChangeRowsPerPage} />
        </div>
      )}

      <Dialog open={openCreate} onClose={() => setOpenCreate(false)}>
        <DialogTitle>Crear Cliente</DialogTitle>
        <DialogContent>
          <CreateCliente
            lstTipoIdentificacion={lstTipoIdentificacion}
            onClose={() => {
              setOpenCreate(false);
              fetchClientes();
            }}
          />
        </DialogContent>
      </Dialog>

      <Dialog open={openEdit} onClose={() => setOpenEdit(false)}>
        <DialogTitle>Editar Cliente</DialogTitle>
        <DialogContent>
          <EditCliente
            lstTipoIdentificacion={lstTipoIdentificacion}
            cliente={selectedCliente}
            onClose={() => {
              setOpenEdit(false);
              fetchClientes();
            }}
          />
        </DialogContent>
      </Dialog>

      <Dialog open={openView} onClose={() => setOpenView(false)}>
        <DialogTitle>Ver Cliente</DialogTitle>
        <DialogContent>
          <ViewCliente cliente={selectedCliente} onClose={() => setOpenView(false)} />
        </DialogContent>
      </Dialog>

      <Dialog open={openProductos} onClose={() => setOpenProductos(false)} maxWidth='sm' fullWidth>
        <DialogTitle>Productos Asignados</DialogTitle>
        <DialogContent>
          <AssignProductos cliente={selectedCliente} onClose={() => setOpenProductos(false)} />
        </DialogContent>
      </Dialog>

      <ConfirmDelete open={openConfirmDelete} onClose={handleCloseDeleteConfirm} onConfirm={handleConfirmDelete} message='¿Estás seguro de que deseas eliminar este cliente?' />
    </div>
  );
};

export default Clientes;
